import axios from 'axios';

// API Configuration
const API_BASE_URL = import.meta.env.PROD
  ? 'https://admin.clarityofconcept.com/api'  // Admin backend API
  : 'http://localhost:8000';  // Local development

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Admin API endpoints
export const adminApi = {
  // Authentication
  login: (email: string, password: string) =>
    api.post('/admin/login', { email, password }),
  
  logout: () => api.post('/admin/logout'),
  
  verify: () => api.get('/admin/verify'),
  
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  
  // Notifications
  previewNotification: (data: any) =>
    api.post('/admin/notifications/preview', data),
  
  sendNotification: (data: any) =>
    api.post('/admin/notifications/send', data),
  
  getTemplates: () => api.get('/admin/notifications/templates'),
  
  createTemplate: (data: any) =>
    api.post('/admin/notifications/templates', data),
  
  deleteTemplate: (id: number) =>
    api.delete(`/admin/notifications/templates/${id}`),
  
  // Users
  getUsers: (params?: any) =>
    api.get('/admin/notifications/users', { params }),
  
  // Analytics
  getNotificationAnalytics: (notificationId: number) =>
    api.get(`/admin/notifications/analytics/${notificationId}`),
  
  // Media
  uploadMedia: (file: File, mediaType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', mediaType);
    
    return api.post('/admin/notifications/upload-media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Materials Management
  getSubjectsAndChapters: () => 
    api.get('/admin/materials/subjects-chapters'),
  
  uploadMaterial: (formData: FormData) =>
    api.post('/admin/materials/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  getMaterials: () => 
    api.get('/admin/materials/list'),
  
  deleteMaterial: (id: number) =>
    api.delete(`/admin/materials/${id}`),

  // Topic & Chapter Position Management
  // Topic position management
  getTopicsWithPositions: (chapterId: number) =>
    api.get(`/admin/ordering/chapters/${chapterId}/topics-with-positions`),

  reorderTopics: (chapterId: number, topicIds: number[]) =>
    api.post(`/admin/ordering/chapters/${chapterId}/reorder-topics`, { topic_ids: topicIds }),

  updateTopicPosition: (topicId: number, position: number) =>
    api.put(`/admin/ordering/topics/${topicId}/position`, { position }),

  resetTopicOrder: (chapterId: number) =>
    api.post(`/admin/ordering/chapters/${chapterId}/reset-topic-order`),

  swapTopics: (topicId1: number, topicId2: number) =>
    api.post('/admin/ordering/topics/swap', { topic_id_1: topicId1, topic_id_2: topicId2 }),

  addTopicWithPosition: (data: { name: string; chapter_id: number; position?: number; description?: string }) =>
    api.post('/admin/ordering/topics', data),

  // Chapter position management
  getChaptersWithPositions: (subjectId: number) =>
    api.get(`/admin/ordering/subjects/${subjectId}/chapters-with-positions`),

  reorderChapters: (subjectId: number, chapterIds: number[]) =>
    api.post(`/admin/ordering/subjects/${subjectId}/reorder-chapters`, { chapter_ids: chapterIds }),

  updateChapterPosition: (chapterId: number, position: number) =>
    api.put(`/admin/ordering/chapters/${chapterId}/position`, { position }),

  // Utility
  normalizeAllPositions: () =>
    api.post('/admin/ordering/normalize-all-positions'),

  // Subjects (UPSC content) — Admin controls
  listSubjectArticles: (params: {
    publish_status?: 'draft' | 'published' | 'archived' | 'updated';
    language_code?: string;
    subject_slug?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }) => api.get('/admin/subjects/articles', { params }),

  listSubjects: (params: { language_code?: string }) =>
    api.get('/admin/subjects/list', { params }),

  listSubjectTopics: (params: { subject_id: number; language_code?: string }) =>
    api.get('/admin/subjects/topics', { params }),

  getSubjectArticle: (articleId: number) =>
    api.get(`/admin/subjects/articles/${articleId}`),

  updateSubjectArticle: (articleId: number, patch: {
    article_title?: string;
    h1_heading?: string;
    meta_title?: string;
    meta_description?: string;
    excerpt?: string | null;
    content?: string;
    focus_keyword?: string | null;
    secondary_keywords?: string | null;
    publish_status?: 'draft' | 'published' | 'archived';
  }) => api.patch(`/admin/subjects/articles/${articleId}`, patch),

  updateSubjectArticleStatus: (articleId: number, publish_status: 'draft' | 'published') =>
    adminApi.updateSubjectArticle(articleId, { publish_status }),

  deleteSubjectArticle: (articleId: number, confirm_slug: string) =>
    api.delete(`/admin/subjects/articles/${articleId}`, { params: { confirm_slug } }),

  createSubjectArticle: (data: {
    subject_id: number;
    topic_id: number;
    language_code: string;
    slug: string;
    article_title: string;
    h1_heading?: string | null;
    meta_title: string;
    meta_description: string;
    excerpt?: string | null;
    content: string;
    focus_keyword?: string | null;
    secondary_keywords?: string | null;
  }) => api.post('/admin/subjects/articles', data),

  // Billing (entitlements + product catalog)
  getEntitlementLimits: () =>
    api.get('/admin/billing/entitlement-limits'),

  updateEntitlementLimit: (payload: {
    entitlement_key: string;
    notes_storage_mb: number;
    notes_max_upload_mb: number;
    notes_media_sync_enabled: boolean;
    notes_large_media_enabled: boolean;
  }) => api.put('/admin/billing/entitlement-limits', payload),

  getBillingProducts: (params?: { platform?: string; active?: boolean }) =>
    api.get('/admin/billing/products', { params }),

  updateBillingProduct: (payload: {
    product_id: string;
    entitlement_key: string;
    duration_days: number;
    platform: 'ios' | 'android' | 'web';
    active: boolean;
  }) => api.put('/admin/billing/products', payload),

  getUserEntitlements: (userId: number) =>
    api.get('/admin/billing/user-entitlements', { params: { user_id: userId } }),

  updateUserEntitlement: (payload: {
    user_id: number;
    entitlement_key: string;
    status: 'active' | 'grace' | 'expired' | 'revoked';
    expires_at?: string | null;
  }) => api.put('/admin/billing/user-entitlements', payload),

  getUserOverrides: (userId: number) =>
    api.get('/admin/billing/user-overrides', { params: { user_id: userId } }),

  updateUserOverride: (payload: {
    user_id: number;
    entitlement_key: string;
    notes_storage_mb_override?: number | null;
    notes_max_upload_mb_override?: number | null;
    notes_media_sync_enabled_override?: boolean | null;
    notes_large_media_enabled_override?: boolean | null;
  }) => api.put('/admin/billing/user-overrides', payload),
};
